import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { DbProduct, DbProductOverview, ProductDetailsCard, ProductDetailsProduct, getMissingProductCard } from '../../models/Product'
import { CalculatorIcon, CalendarDaysIcon, FlagIcon } from '@heroicons/react/24/outline'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { ErrorComponent } from '../../components/ErrorComponent/ErrorComponent'
import { AppState } from '../../appSlice'
import { IconDescription } from '../../components/IconDescription/IconDescription'
import WarningAlert from '../../components/Alerts/WarningAlert'
import { ProductOverviewLink } from '../../components/ItemOverviewLink/ItemOverviewLink'
import { applyProductFilters, getDefaultProductFilterState } from '../../components/ItemDisplay/Product/functions'
import { AppProgress } from '../../components/Progress/AppProgress'
import { Helmet } from 'react-helmet'
import { CardVariantImage } from '../../components/CardVariantImage/CardVariantImage'
import { CardVariantImageLinkOverlay } from '../../components/CardVariantImage/CardVariantImageLinkOverlay'
import { CardVariantImagePlaceholderOverlay } from '../../components/CardVariantImage/CardVariantImagePlaceholderOverlay'
import { CardVariantImageCardBackOverlay } from '../../components/CardVariantImage/CardVariantImageCardBackOverlay'
import { CardVariantImageProductFooter } from '../../components/CardVariantImage/CardVariantImageProductFooter'
import { Spinner } from '../../components/Spinner/Spinner'
import ItemDisplayPage from '../../components/ItemDisplay/ItemDisplayPage'
import CardFilterModalBody, { CardFilterModalBodyOptionalProps } from '../../components/ItemDisplay/Card/CardFilterModalBody'
import ProductFilterModalBody from '../../components/ItemDisplay/Product/ProductFilterModalBody'
import { UserSettings } from '../../models/UserSettings'
import { FIREBASE_STORAGE_URL } from '../../app-settings'
import { ContentContainer } from '../../components/ContentContainer/ContentContainer'
import { SquareEnixLogo } from '../../components/Icons/SquareEnixLogo'
import { getProduct } from '../../repository/productRepository'
import { showFailure } from '../../components/NotificationStack/notificationSlice'
import { FoilOverlay } from '../../components/CardVariantImage/FoilOverlay'
import { filterCards, getDefaultCardFilterState } from '../../functions/filter/CardFilterCommands'

export default function ProductDetailsPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState<DbProductOverview | undefined>(undefined);
  const [productDetails, setProductDetails] = useState<DbProduct>();
  const [productsInProduct, setProductsInProduct] = useState<ProductDetailsProduct[]>([]);
  const [cards, setCards] = useState<ProductDetailsCard[]>([]);
  const [filterStateIdProducts, setFilterStateIdProducts] = useState(`productDetails-${productId}-products`);
  const [filterStateIdCards, setFilterStateIdCards] = useState(`productDetails-${productId}-cards`);
  const [isProductDetailsLoading, setIsProductDetailsLoading] = useState(true);
  const userSettings = useSelector<RootState, UserSettings>(state => state.user.userSettings);

  const [missingPrices, setMissingPrices] = useState<number>(0);
  const [productValue, setProductValue] = useState<number>(0);

  const {
    products,
    productIdDictionary,
    variantCache,
    isProductsLoading,
    variantPriceDictionary
  } = useSelector<RootState, AppState>(store => store.app);

  useEffect(() => {
    if (!products || !products.length || !productId || !variantCache?.any?.all?.list?.length) {
      return;
    }

    const matchingProduct = productIdDictionary[parseInt(productId)];
    if (!matchingProduct) {
      return;
    }

    setProduct(matchingProduct);
    getProduct(productId)
      .then((result) => {
        const details = result;
        setProductDetails(details);

        const newProductsInProduct: ProductDetailsProduct[] = [];
        if (details.products) {
          for (const productJson of details.products) {
            const detailsProduct: ProductDetailsProduct = {
              ...productJson,
              ...productIdDictionary[productJson.productId]
            }

            newProductsInProduct.push(detailsProduct);
          }
        }

        setProductsInProduct(newProductsInProduct);

        if (details.cards) {
          const newCardsInProduct: ProductDetailsCard[] = [];
          for (const card of details.cards) {
            const variant = variantCache.any.all.byId[card.variantId];

            const cardDetails: ProductDetailsCard = variant
              ? {
                ...card,
                ...variant
              }
              : getMissingProductCard(card);

            newCardsInProduct.push(cardDetails);
          }

          const sortedCardsInProduct = filterCards([...newCardsInProduct], getDefaultCardFilterState({
            userSettings: userSettings,
            primarySerialOnly: userSettings.primarySerialOnly
          }));

          setCards(sortedCardsInProduct);
        }

        setFilterStateIdProducts(`productDetails-${productId}-products`);
        setFilterStateIdCards(`productDetails-${productId}-cards`);
        setIsProductDetailsLoading(false);
      })
      .catch((e) => {
        showFailure("Error fetching product.");
      })
      .finally(() => {

      });
  }, [products, productId, variantCache]);

  useEffect(() => {
    if (!cards || !cards.length || !variantPriceDictionary) {
      setMissingPrices(0);
      setProductValue(0);
      return;
    }

    let newProductValue = 0;
    let newMissingPrices = 0;
    for (const card of cards) {
      if (!card.quantity) {
        continue;
      }

      const price = variantPriceDictionary[card.id];
      if (!price) {
        newMissingPrices += 1;
        continue;
      }

      for (let i = 0; i < card.quantity; i++) {
        newProductValue += price.price;
      }
    }

    setMissingPrices(newMissingPrices);
    setProductValue(newProductValue);
  }, [variantPriceDictionary, cards]);

  if (isProductsLoading) {
    return (
      <AppProgress showProducts={true} />
    );
  }

  if (isProductDetailsLoading) {
    return <div className='m-auto mt-12'><Spinner /></div>
  }

  if (!productId || isNaN(parseInt(productId)) || !product) {
    return <ErrorComponent title="Invalid Product" message='The Product ID provided is invalid.' />;
  }

  const description = (className: string) => (
    <div className={className}>
      {
        productDetails
          ? <p className='mt-6' dangerouslySetInnerHTML={{ __html: productDetails.description }} />
          : <div className="animate-pulse">
            <div className="flex-1 space-y-6 py-1">
              <div className="h-2 bg-gray-400 rounded"></div>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-4">
                  <div className="h-2 bg-gray-400 rounded col-span-2"></div>
                  <div className="h-2 bg-gray-400 rounded col-span-1"></div>
                </div>
                <div className="h-2 bg-gray-400 rounded"></div>
              </div>
            </div>
          </div>
      }

      {
        !!productDetails?.purchaseLinks?.length &&
        <div className="mt-8 border-gray-200 border-t border-b py-8">
          <div className="flex gap-8 items-center">
            {
              productDetails.purchaseLinks.map((link, index) => (
                <a key={index} href={link.url} target="_blank">
                  <VendorImage vendor={link.vendor} />
                </a>
              ))
            }
          </div>
        </div>
      }
    </div>
  )

  return (
    <ContentContainer className="flex flex-col rounded px-4 md:px-8" classNames={{ childrenContainer: "mt-0" }}>
      <Helmet>
        {
          product.name &&
          <title>{product.name} - Materia Hunter</title>
        }
      </Helmet>
      <div className="flex flex-col-reverse md:flex-row gap-8 mb-12">
        <div className="flex-1">
          <div className='flex flex-col gap-4'>
            <div className="h-56 w-full overflow-hidden rounded-md shadow-sm bg-secondary border border-color-border/30 group-hover:opacity-75 lg:h-72 xl:h-80">
              <img
                src={`${FIREBASE_STORAGE_URL}/${product.imageUrl}`}
                loading="lazy"
                alt={`${product.name} image`}
                className="h-full w-full object-contain p-5 object-center" />
            </div>
          </div>

          {
            description("block md:hidden mt-8")
          }
        </div>
        <div style={{ flex: 3 }}>
          <div className="max-w-4xl">
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl mb-2">
              {product.name}
            </h1>

            <div className='flex mb-8 gap-8'>
              <IconDescription
                description={product.released}
                icon={CalendarDaysIcon}
                iconAccessibilityText="Release Date"
              />

              {
                productValue > 0 &&
                <IconDescription
                  className='!text-green-600'
                  description={`$${productValue.toFixed(2)}`}
                  icon={CalculatorIcon}
                  iconAccessibilityText="Estimated Value of Cards In Product"
                />
              }

              <a href="https://discord.gg/6Nxnuf2jWY" target='_blank' className=''>
                <IconDescription
                  description={""}
                  icon={FlagIcon}
                  iconAccessibilityText="Report a Bug"
                />
              </a>
            </div>

            {
              description("hidden md:block")
            }
          </div>
        </div>
      </div>
      <div className="w-full lg:col-span-4 mt-12 lg:max-w-none space-y-12">
        {
          !!productDetails?.products?.length &&
          <div>
            <h3 className='font-bold text-xl mb-4'>Products</h3>
            <ItemDisplayPage
              toolbarPosition='relative'
              displayStateId={filterStateIdProducts}
              items={productsInProduct}
              filter={(items, filters) => applyProductFilters(items, filters)}
              getDefaultFilterState={getDefaultProductFilterState}
              modalBody={ProductFilterModalBody}
              itemComponentGenerator={(item) => {
                return (
                  <ProductOverviewLink
                    key={item.id}
                    description={item.name ?? ""}
                    imageAlt={item.name ?? ""}
                    imageSrc={item.imageUrl ? `${FIREBASE_STORAGE_URL}/${item.imageUrl}` : ""}
                    linkTo={`/products/${item.id}`}
                    subText={`${item.quantity} included`} />
                )
              }} />
          </div>
        }
        {
          !!cards.length &&
          <div>
            <h3 className='font-bold text-xl  mb-4'>Cards</h3>
            <ItemDisplayPage
              toolbarPosition='relative'
              enableScrollRefresh={true}
              displayStateId={filterStateIdCards}
              items={cards}
              getDefaultFilterState={(userSettings) => getDefaultCardFilterState({
                userSettings: userSettings,
                primarySerialOnly: userSettings.primarySerialOnly,
                priceSorting: true
              })}
              filter={(items, filters) => filterCards(items, filters, { prices: variantPriceDictionary })}
              modalBody={CardFilterModalBody}
              modalBodyOptionalProps={{
                showVariantTypeFilter: true
              } as CardFilterModalBodyOptionalProps}
              itemComponentGenerator={(item) => {
                return (
                  <CardVariantImage
                    key={item.id}
                    variant={item}
                    overlay={
                      <>
                        <FoilOverlay variant={item} />
                        <CardVariantImageLinkOverlay variant={item} className="z-[9]" />
                        <CardVariantImagePlaceholderOverlay variant={item} className="z-[2]" />
                        <CardVariantImageCardBackOverlay className="z-[0]" />
                      </>
                    }
                    footer={
                      <>
                        <CardVariantImageProductFooter productDetailsCard={item} variantPriceDictionary={variantPriceDictionary} />
                      </>
                    } />
                )
              }} />
          </div>
        }
        {
          // isFinite will convert date to a number (time) and return true if it's a valid number. If the date does not parse, isFinite returns false.
          // Basically a hacky way to check if a date is valid or not.
          (
            productDetails?.cards.length === 0 &&
            productDetails?.products.length === 0 &&
            (
              !isFinite(new Date(product.released) as unknown as number) ||
              (new Date(product.released)).getTime() > Date.now()
            )
          ) &&
          <div className="mt-5">
            <WarningAlert text="This product is not released yet. Check back soon for more details on what's included within!" />
          </div>
        }
      </div>
    </ContentContainer>
  )
}

type VendorImageProps = {
  vendor: string
}

function VendorImage(props: VendorImageProps) {
  switch (props.vendor) {
    case "TCGplayer":
      return <img className="max-h-8" style={{ maxWidth: "5rem" }} alt={props.vendor} src={`${FIREBASE_STORAGE_URL}/images/vendors/TCGplayer.png`} />
    case "SquareEnixStore":
      return <SquareEnixLogo className='max-h-8 max-w-[8rem]' />
    default:
      return null;
  }
}

const vendorImageMap: Record<string, string> = {
  "TCGplayer": "TCGplayer.png",
  "SquareEnixStore": "SquareEnixStore.svg"
}